import { createEffect, createSignal, on, useContext } from "solid-js";
import { playerContext } from "../..";
import useCanSkip from "../../hooks/useCanSkip";
import useContinue from "../../hooks/useContinue";
import useLiveMode from "../../hooks/useLiveMode";
import useTrackSource from "../../hooks/useTrackSource";
import useSaveState from "./hooks/useSaveState";
import type { AudioPlayerProps } from "./types";

const AudioPlayer: AudioPlayerProps = (props) => {
	const [trackSource, setTrackSource] = createSignal<string | undefined>();
	const { player, setPlayer } = useContext(playerContext);
	const getTrackSource = useTrackSource();
	const { canGoNext, canGoPrevious } = useCanSkip();
	const handlPreload = useContinue();
	useLiveMode();
	useSaveState();
	let audioRef: HTMLAudioElement | undefined;

	const handleTogglePlayer = (resume?: boolean) => {
		if (!audioRef) return;
		if (!resume) audioRef.currentTime = 0;
		if (audioRef.paused) audioRef.play();
		else {
			audioRef.pause();
		}
	};

	const handleSetProgress = (progress: number) => {
		const length = player.queue[player.track]?.length ?? 0;
		if (!audioRef || !length) return;

		audioRef.currentTime = length * progress;
	};

	const onLoaded = async () => {
		if (!audioRef) return;
		audioRef.removeEventListener("loadedmetadata", onLoaded);
		try {
			await audioRef.play();

			setPlayer("queue", player.track, "length", audioRef.duration);
		} catch {
			if (canGoNext()) setPlayer("track", (s) => s + 1);
		}
	};

	createEffect(
		on(
			() => player.track,
			() => {
				if (!audioRef) return;
				audioRef.muted = true;

				if (player.track < 0) {
					setTrackSource(undefined);
					return;
				}
				setPlayer("loading", true);
				getTrackSource()
					.then((source) => {
						setTrackSource(source);
						handlPreload();
						if (audioRef?.paused && player.resume) audioRef.addEventListener("loadedmetadata", onLoaded);
					})
					.catch(() => setPlayer("disabled", true))
					.finally(() => {
						setPlayer("loading", false);
						setPlayer("resume", true);
					});
			},
		),
	);

	const handleOnLoad = () => setPlayer("loading", false);
	const handleOnEnded = () => {
		setTrackSource(undefined);
		setPlayer("track", (s) => s + 1);
	};

	return (
		<>
			<audio
				src={trackSource()}
				onload={handleOnLoad}
				ontimeupdate={(e) => setPlayer("time", e.currentTarget.currentTime)}
				onpause={() => setPlayer("playing", false)}
				onplay={(e) => {
					e.currentTarget.muted = false;
					setPlayer("playing", true);
					setPlayer("disabled", false);
				}}
				onended={handleOnEnded}
				ref={audioRef}
			/>
			{props.children(handleTogglePlayer, canGoNext, canGoPrevious, handleSetProgress)}
		</>
	);
};
export default AudioPlayer;
